import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellOff, Check, Copy, Mail, MessageCircle, Sparkles, Undo2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { formatDate } from '@/lib/dates';
import { whatsappNumber } from '@/features/invoices/share';
import { Badge, Button, Card, CardContent, EmptyState, ErrorState, Input, Money, Textarea } from '@/features/common/ui';
import type { CfoSettings, ChaseList, ChaseRow, ChaseStep } from './types';

const STATE_LABEL: Record<ChaseRow['state'], string> = { due: 'To send', resting: 'Resting', waiting: 'Not yet', snoozed: 'Snoozed', held: 'Paused' };

/**
 * Chase: every unpaid invoice, where it stands on the reminder ladder, and
 * the words for the step that is due today.
 *
 * Sending is always a person's act: WhatsApp opens on the customer's number
 * with the words filled in, and the reminder is recorded when they say it
 * went; email goes from the server with the PDF attached; copy is for
 * anything else. Recording a step is what moves the ladder on, and a step
 * is recorded once.
 */
export function ChaseTab() {
  const org = useActiveOrg();
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: ['cfo-chase', org?.id], queryFn: () => api.get<ChaseList>('/cfo/chase'), enabled: !!org?.id });
  const settings = useQuery({ queryKey: ['cfo-settings', org?.id], queryFn: () => api.get<CfoSettings>('/cfo/settings'), enabled: !!org?.id });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['cfo-chase', org?.id] });
    void queryClient.invalidateQueries({ queryKey: ['cfo-settings', org?.id] });
  };
  const patch = useMutation({ mutationFn: (chase: Partial<CfoSettings['chase']>) => api.patch('/cfo/settings', { chase }), onSuccess: refresh });

  if (list.isError) return <Card><ErrorState error={list.error} onRetry={() => list.refetch()} /></Card>;
  if (!list.data) return <div className="skeleton h-48 rounded-lg" />;
  const data = list.data;
  const due = data.rows.filter((r) => r.state === 'due');
  const rest = data.rows.filter((r) => r.state !== 'due');
  const canEdit = !!settings.data?.canEdit;
  const skip = (customerId: string) => patch.mutate({ skipCustomerIds: [...(settings.data?.chase.skipCustomerIds ?? []), customerId] });

  return (
    <div className="space-y-6">
      <section className="space-y-3" aria-labelledby="chase-due">
        <h2 id="chase-due" className="text-sm font-semibold">
          To send today {due.length ? `(${due.length})` : ''}
        </h2>
        {due.length === 0 ? (
          <Card>
            <EmptyState title="Nothing to chase today" description="Every unpaid invoice is either not late enough, resting after a payment, or already reminded for this step." />
          </Card>
        ) : (
          <div className="grid gap-3 xl:grid-cols-2">
            {due.map((row) => (
              <DueCard key={row.invoiceId} row={row} data={data} onDone={refresh} onSkipCustomer={canEdit ? () => skip(row.customer.id) : undefined} />
            ))}
          </div>
        )}
      </section>

      {rest.length ? (
        <section className="space-y-2" aria-labelledby="chase-rest">
          <h2 id="chase-rest" className="text-sm font-semibold">
            The rest of what you are owed
          </h2>
          <Card>
            <ul className="divide-y divide-border">
              {rest.map((row) => (
                <RestRow key={row.invoiceId} row={row} onDone={refresh} />
              ))}
            </ul>
          </Card>
        </section>
      ) : null}

      <Card>
        <CardContent className="space-y-3 pt-4 text-sm">
          <h2 className="font-semibold">The ladder</h2>
          <ul className="space-y-1">
            {data.ladder.map((l) => (
              <li key={l.step} className="flex items-center justify-between gap-3">
                <span>
                  {l.label} <span className="text-muted-foreground">({l.offset < 0 ? `${-l.offset} days before due` : `${l.offset} days late`})</span>
                </span>
                {canEdit ? (
                  <label className="inline-flex items-center gap-2 touch:min-h-11">
                    <input type="checkbox" checked={l.enabled} onChange={(e) => patch.mutate({ [l.step]: e.target.checked })} data-chase-step-toggle={l.step} />
                    <span className="text-xs text-muted-foreground">{l.enabled ? 'On' : 'Off'}</span>
                  </label>
                ) : (
                  <span className="text-xs text-muted-foreground">{l.enabled ? 'On' : 'Off'}</span>
                )}
              </li>
            ))}
          </ul>
          {data.skipped.length ? (
            <div className="space-y-1">
              <p className="font-medium">Never chased</p>
              <ul className="flex flex-wrap gap-2">
                {data.skipped.map((c) => (
                  <li key={c.id}>
                    <Badge variant="outline">
                      {c.name}
                      {canEdit ? (
                        <button
                          type="button"
                          className="ml-2 text-primary hover:underline"
                          onClick={() => patch.mutate({ skipCustomerIds: (settings.data?.chase.skipCustomerIds ?? []).filter((id) => id !== c.id) })}
                        >
                          Chase again
                        </button>
                      ) : null}
                    </Badge>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <p className="text-xs text-muted-foreground">
            A customer who paid part since the last reminder rests for a week. No reminder mentions credit bureaus, lawyers or recovery agents.
            {data.emailEnabled ? '' : ' Email is not set up on this server, so reminders go by WhatsApp or copy.'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function DueCard({ row, data, onDone, onSkipCustomer }: { row: ChaseRow; data: ChaseList; onDone: () => void; onSkipCustomer?: () => void }) {
  const [channel, setChannel] = useState<'whatsapp' | 'email'>(data.emailEnabled && row.customer.email ? 'email' : 'whatsapp');
  const [subject, setSubject] = useState(row.text?.subject ?? '');
  const [body, setBody] = useState(channel === 'email' ? row.text?.email ?? '' : row.text?.whatsapp ?? '');
  const [to, setTo] = useState(row.customer.email ?? '');
  const [opened, setOpened] = useState<null | 'whatsapp' | 'copy'>(null);
  const step = row.step as ChaseStep;

  const record = useMutation({
    mutationFn: (input: { status: 'sent' | 'skipped'; channel?: 'whatsapp' | 'copy' }) =>
      api.post(`/cfo/chase/${row.invoiceId}/${step}`, { ...input, recipient: input.channel === 'whatsapp' ? row.customer.phone : null, subject, body }),
    onSuccess: onDone,
  });
  const email = useMutation({ mutationFn: () => api.post(`/cfo/chase/${row.invoiceId}/${step}/email`, { to, subject, body }), onSuccess: onDone });
  const claude = useMutation({ mutationFn: () => api.post(`/cfo/chase/${row.invoiceId}/${step}/claude`), onSuccess: onDone });
  const snooze = useMutation({ mutationFn: () => api.put(`/cfo/chase/${row.invoiceId}/snooze`), onSuccess: onDone });
  const error = [record.error, email.error, claude.error, snooze.error].find((e) => e instanceof ApiError) as ApiError | undefined;

  const switchTo = (next: 'whatsapp' | 'email') => {
    setChannel(next);
    setBody(next === 'email' ? row.text?.email ?? '' : row.text?.whatsapp ?? '');
  };
  const openWhatsapp = () => {
    const number = whatsappNumber(row.customer.phone);
    window.open(`https://wa.me/${number ?? ''}?text=${encodeURIComponent(body)}`, '_blank', 'noopener');
    setOpened('whatsapp');
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(channel === 'email' ? `${subject}\n\n${body}` : body);
    } catch {
      // The text is on screen to select by hand.
    }
    setOpened('copy');
  };

  return (
    <Card data-chase-row={row.number} data-chase-step={row.step}>
      <CardContent className="space-y-3 pt-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <p className="font-semibold">{row.customer.name}</p>
            <p className="text-xs text-muted-foreground">
              <Link to={`/invoices/${row.invoiceId}`} className="inline-flex items-center text-primary hover:underline touch:min-h-11">
                {row.number}
              </Link>{' '}
              · due {formatDate(row.dueDate)} · {row.daysLate > 0 ? `${row.daysLate} days late` : row.daysLate === 0 ? 'due today' : `due in ${-row.daysLate} days`}
            </p>
          </div>
          <div className="text-right">
            <Money value={row.balance} currency={row.currency} className="font-semibold" />
            <p className="text-xs">
              <Badge variant={step === 'final' ? 'danger' : step === 'firm' ? 'warning' : 'info'}>{row.stepLabel}</Badge>
            </p>
          </div>
        </div>
        {row.history.length ? (
          <p className="text-xs text-muted-foreground">
            Before: {row.history.map((h) => `${h.step} ${h.status === 'sent' ? `by ${h.channel ?? 'hand'}` : 'skipped'} on ${formatDate(h.on)}`).join('; ')}
          </p>
        ) : null}
        {row.msme ? (
          <p className="text-xs text-muted-foreground">
            MSMED interest so far: about {row.msme.interest} at {row.msme.annualPercent}% from {formatDate(row.msme.fromDate)}. {row.msme.bankRateNote}
          </p>
        ) : null}

        <div role="group" aria-label="Send by" className="flex gap-1">
          <Button size="sm" variant={channel === 'whatsapp' ? 'secondary' : 'ghost'} onClick={() => switchTo('whatsapp')}>
            <MessageCircle className="size-4" aria-hidden /> WhatsApp
          </Button>
          <Button size="sm" variant={channel === 'email' ? 'secondary' : 'ghost'} onClick={() => switchTo('email')}>
            <Mail className="size-4" aria-hidden /> Email
          </Button>
          {row.wording === 'claude' ? (
            <span className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Sparkles className="size-3" aria-hidden /> Worded by Claude
            </span>
          ) : null}
        </div>
        {channel === 'email' ? (
          <>
            <Input label="To" type="email" value={to} onChange={(e) => setTo(e.target.value)} />
            <Input label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </>
        ) : null}
        <Textarea label="Message" rows={channel === 'email' ? 10 : 5} value={body} onChange={(e) => setBody(e.target.value)} />

        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error.message}
          </p>
        ) : null}
        {data.canSend ? (
          <div className="flex flex-wrap items-center gap-2">
            {channel === 'whatsapp' ? (
              opened === 'whatsapp' ? (
                <Button size="sm" onClick={() => record.mutate({ status: 'sent', channel: 'whatsapp' })} loading={record.isPending} data-chase-mark-sent>
                  <Check className="size-4" aria-hidden /> It went - mark sent
                </Button>
              ) : (
                <Button size="sm" onClick={openWhatsapp} data-chase-whatsapp>
                  <MessageCircle className="size-4" aria-hidden /> Open WhatsApp
                </Button>
              )
            ) : data.emailEnabled ? (
              <Button size="sm" onClick={() => email.mutate()} loading={email.isPending} disabled={!to}>
                <Mail className="size-4" aria-hidden /> Send email
              </Button>
            ) : null}
            {opened === 'copy' ? (
              <Button size="sm" variant="outline" onClick={() => record.mutate({ status: 'sent', channel: 'copy' })} loading={record.isPending}>
                <Check className="size-4" aria-hidden /> Sent it - mark sent
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={() => {
                void copy();
              }}>
                <Copy className="size-4" aria-hidden /> Copy
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => claude.mutate()} loading={claude.isPending} disabled={row.ai?.status === 'queued' || row.ai?.status === 'claimed'}>
              <Sparkles className="size-4" aria-hidden />
              {row.ai?.status === 'queued' || row.ai?.status === 'claimed' ? 'Waiting for Claude' : 'Ask Claude to word it'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => record.mutate({ status: 'skipped' })} disabled={record.isPending}>
              Skip this step
            </Button>
            <Button size="sm" variant="ghost" onClick={() => snooze.mutate()} loading={snooze.isPending}>
              <BellOff className="size-4" aria-hidden /> Snooze 2 weeks
            </Button>
            {onSkipCustomer ? (
              <Button size="sm" variant="ghost" onClick={onSkipCustomer}>
                Never chase {row.customer.name}
              </Button>
            ) : null}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Only someone who can edit invoices can send reminders.</p>
        )}
        {row.ai?.status === 'failed' ? <p className="text-xs text-destructive">Claude could not word it: {row.ai.error}</p> : null}
      </CardContent>
    </Card>
  );
}

function RestRow({ row, onDone }: { row: ChaseRow; onDone: () => void }) {
  const unsnooze = useMutation({ mutationFn: () => api.delete(`/cfo/chase/${row.invoiceId}/snooze`), onSuccess: onDone });
  const last = row.history.at(-1);
  const undo = useMutation({ mutationFn: () => api.delete(`/cfo/chase/${row.invoiceId}/${last?.step}`), onSuccess: onDone });
  const when =
    row.state === 'held'
      ? row.heldUntil
        ? `Payment expected ${formatDate(row.heldUntil)}; no reminders until then`
        : 'Payment not expected soon; reminders paused on the invoice'
      : row.state === 'resting'
      ? `Paid part recently; resting until ${formatDate(row.restingUntil)}`
      : row.state === 'snoozed'
        ? `Snoozed until ${formatDate(row.snoozedUntil)}`
        : row.nextOn
          ? `Next reminder ${formatDate(row.nextOn)}`
          : 'Every reminder has gone';
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm" data-chase-rest={row.number}>
      <div className="min-w-0">
        <p className="font-medium">
          {row.customer.name}{' '}
          <Link to={`/invoices/${row.invoiceId}`} className="inline-flex items-center text-xs text-primary hover:underline touch:min-h-11">
            {row.number}
          </Link>
        </p>
        <p className="text-xs text-muted-foreground">
          <Badge variant="neutral">{STATE_LABEL[row.state]}</Badge> {when}
          {last ? ` · last: ${last.step} ${last.status} ${formatDate(last.on)}` : ''}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Money value={row.balance} currency={row.currency} />
        {row.state === 'snoozed' ? (
          <Button size="sm" variant="ghost" onClick={() => unsnooze.mutate()} loading={unsnooze.isPending}>
            Wake
          </Button>
        ) : last ? (
          <Button size="sm" variant="ghost" onClick={() => undo.mutate()} loading={undo.isPending} aria-label={`Undo the ${last.step} reminder`}>
            <Undo2 className="size-4" aria-hidden />
          </Button>
        ) : null}
      </div>
    </li>
  );
}
